import React, { useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Typography,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { VenueProvider } from '../../context/VenueContext';
import venueService from '../../services/venueService';

// Phase 4.H — "Wine Lists" replaced by "Menus" as the canonical menu-family
// surface. The /wine-lists route still resolves (legacy detail pages remain
// reachable by direct URL) but it is no longer in the primary tab strip.
//
// Phase 4.J — "Production Recipes" and "Production Orders" removed from the
// primary tab row. They were ERP/manufacturing concepts that didn't belong
// alongside Menus / Inventory / Purchasing in daily restaurant UX. Routes
// (/recipes, /production-orders) remain mounted and reachable by direct URL.
const VENUE_TABS = [
  { label: 'Overview',     path: '' },
  { label: 'Menus',        path: '/menus' },
  { label: 'Inventory',    path: '/inventory' },
  { label: 'Purchasing',   path: '/purchasing' },
  // PO-SHEET.1c — SUPPLIER-CONNECT shipped its screens without a door: the
  // venue supplier list was reachable only by typing the URL, which is also
  // why a venue can look like it has no suppliers when it simply has no way
  // to connect one. It sits beside Purchasing because that is what a supplier
  // is for here.
  { label: 'Suppliers',    path: '/suppliers' },
  { label: 'Stock Counts', path: '/stock-counts' },
  { label: 'Transfers',    path: '/transfers' },
  { label: 'Reports',      path: '/reports' },
  // WAREHOUSE-DETAIL.1 — Warehouses and Locations are gone from the venue tab
  // strip. Both are company-level: the Warehouses page has always listed every
  // warehouse in the company and rendered a who-can-reach-what matrix across
  // all of them, so reaching it through one arbitrary venue said nothing. And a
  // location belongs to a WAREHOUSE — picking a venue first is what made
  // "General Stock" added to Alfred's land in Main Store.
  //
  // Both now live under Stock, addressed by the warehouse that holds them.
  { label: 'QR Page',      path: '/qr-landing-template' },
];

const VenueScopedLayout: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [venueName, setVenueName] = useState('Venue');
  const [defaultCurrency, setDefaultCurrency] = useState('EUR');

  useEffect(() => {
    if (!venueId) return;
    venueService.getVenue(venueId)
      .then(venue => {
        setVenueName(venue.name);
        setDefaultCurrency(venue.defaultCurrency);
      })
      .catch(() => {
        // keep placeholder on error — non-fatal
      });
  }, [venueId]);

  if (!venueId) return null;

  const venueBase = `/admin/venues/${venueId}`;

  const contextValue = {
    venueId,
    venueName,
    defaultCurrency,
  };

  // Determine active tab by matching the current path segment
  const activeTabIndex = (() => {
    const suffix = location.pathname.replace(venueBase, '');
    for (let i = VENUE_TABS.length - 1; i >= 0; i--) {
      if (VENUE_TABS[i].path && suffix.startsWith(VENUE_TABS[i].path)) return i;
    }
    return 0; // Overview
  })();

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    navigate(`${venueBase}${VENUE_TABS[newIndex].path}`);
  };

  return (
    <VenueProvider value={contextValue}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Venue header strip */}
        <Box sx={{ px: 3, pt: 2, pb: 0 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              pt: 2,
              pb: 0,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            {/* Breadcrumb */}
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ mb: 1.5 }}
            >
              <Box
                component={Link}
                to="/admin/venues"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: theme.palette.text.secondary,
                  textDecoration: 'none',
                  fontSize: '0.813rem',
                  fontWeight: 500,
                  '&:hover': { color: theme.palette.primary.main },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 14 }} />
                Venues
              </Box>
              <Typography
                sx={{
                  fontSize: '0.813rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {venueName}
              </Typography>
            </Breadcrumbs>

            {/* Sub-navigation tabs */}
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 44,
                '& .MuiTab-root': {
                  minHeight: 44,
                  fontSize: '0.813rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  px: 2,
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                },
              }}
            >
              {VENUE_TABS.map((tab) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  onClick={() => navigate(`${venueBase}${tab.path}`)}
                />
              ))}
            </Tabs>
          </Paper>
        </Box>

        {/* Page content */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </VenueProvider>
  );
};

export default VenueScopedLayout;
